import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin } from "lucide-react";

const fmtClock = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true }); }
  catch { return iso; }
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const ClockLog = () => {
  const [date, setDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anyEnabled, setAnyEnabled] = useState(false);

  // Hide the log entirely until an admin has flipped time_clock_enabled on
  // at least one employee — otherwise the panel is dead weight.
  useEffect(() => {
    api.get("/employees").then((r) => {
      setAnyEnabled((r.data || []).some((e) => e.time_clock_enabled));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/time-entries?date=${date}`);
      setRows(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 20s so the log updates in real time.
  useEffect(() => {
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <details className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid="clock-log" style={{ display: anyEnabled ? undefined : "none" }}>
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 text-sm">
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
          Time-clock log · {rows.length} punch{rows.length === 1 ? "" : "es"}
        </span>
        <span className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(ev) => setDate(ev.target.value)}
            onClick={(ev) => ev.stopPropagation()}
            data-testid="clock-log-date"
            className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs text-white"
          />
        </span>
      </summary>

      <div className="max-h-80 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="py-6 text-center font-mono text-[11px] uppercase tracking-widest text-white/40">
            No punches for this date yet.
          </p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-[#0b0b0f]">
              <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                <th className="px-4 py-2">Time</th>
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2">Event</th>
                <th className="px-2 py-2">Job</th>
                <th className="px-2 py-2">GPS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <tr key={r.entry_id} data-testid={`clock-log-row-${r.entry_id}`}>
                  <td className="px-4 py-2 font-mono text-white/80">{fmtClock(r.timestamp)}</td>
                  <td className="px-2 py-2 text-white/80">{r.employee_name || "—"}</td>
                  <td className="px-2 py-2 font-mono uppercase tracking-widest text-white/60">
                    {r.event.replace("_", " ")}
                  </td>
                  <td className="px-2 py-2 text-white/70">
                    {r.job_name || (r.job_project_id ? <span className="font-mono text-[10px] text-white/40">{r.job_project_id}</span> : "—")}
                  </td>
                  <td className="px-2 py-2">
                    {r.lat != null && r.lng != null ? (
                      <a
                        href={`https://maps.google.com/?q=${r.lat},${r.lng}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-[10px] text-[#8fa4f0] hover:underline"
                      >
                        <MapPin className="h-3 w-3" />
                        {Number(r.lat).toFixed(4)}, {Number(r.lng).toFixed(4)}
                      </a>
                    ) : (
                      <span className="font-mono text-[10px] text-white/30">no gps</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
};

export default ClockLog;
